// Imports
import React, { useRef, useState, lazy, useContext, useEffect, useCallback, useMemo, Suspense } from 'react';
import uniqueId from 'lodash/uniqueId';
import { ThemeProvider } from 'styled-components/macro';
import helperFunctions from '../../../../../../shareables/foundation/front-end/utils/helper-functions';
import useUpdateOnlyEffect from '../../../../../../shareables/foundation/front-end/utils/use-update-only-effect';
import saveValue from './save-value';
import FieldContainer from '../../../../../../shareables/foundation/front-end/components/forms/field-container';
import { Label } from '../../../../../../shareables/foundation/front-end/components/forms/labels';
import ControlGroup from '../../../../../../shareables/foundation/front-end/components/forms/control-group';
import AddOn from '../../../../../../shareables/foundation/front-end/components/forms/add-on';
import InputContainer from '../../../../../../shareables/foundation/front-end/components/forms/input-container';
import InputIconHolder from '../../../../../../shareables/foundation/front-end/components/forms/input-icon-holder';
import FieldErrorMessage from '../../../../../../shareables/foundation/front-end/components/forms/field-error-message';
import passwordManagerDisablingProps from './password-manager-disabling-props';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import APIConnectedFormContext, { ContextDetails } from '../../../../../contexts/api-connected-form';
import debounce from 'lodash/debounce';
import SuspenseFallback from '../../resources/suspense-fallback';
import { z } from 'zod';
import parsePersistentFormValue from './utils/parse-persistent-form-value';
import BaseFieldProps from './base-field-props';
import isScreenshot from '../../../../../utils/is-screenshot';


// Lazily-loaded components
const PasswordStrengthMeter = lazy(
	() => import('../../../../../../shareables/foundation/front-end/components/forms/password-strength-meter')
);


// Define the accepted props
interface Props extends BaseFieldProps {
	/** The supported values of the `type` attribute of the underlying `<input />` element. */
	type:
		| 'color'
		| 'date'
		| 'datetime-local'
		| 'email'
		| 'hidden'
		| 'month'
		| 'number'
		| 'password'
		| 'range'
		| 'text'
		| 'time'
		| 'url'
		| 'week';
	
	
	/** The initial value state of the field. Will be ignored on subsequent renders. */
	initialValue?: string;
	
	
	/** Decorative icon that will be displayed within the field, before the field text. */
	icon?: IconDefinition;
	
	
	/** Text that will be displayed before the field to augment what kind of value the field provides. */
	prefix?: string;
	
	
	/** Text that will be displayed after the field to augment what kind of value the field provides. */
	suffix?: string;
	
	
	/** Whether to display a password strength meter. This can only be used with `type='password'`. Defaults to `false`. */
	showStrength?: boolean;
	
	
	/** A function that handles a change in value to the field. The function is passed the latest value as its parameter. */
	onChange?: (newValue: string) => void;
	
	
	/** A function that provides the getter for the field to be bound, usually to a `ref`. */
	bindGetter?: (getter: () => string) => void;
	
	
	/** A function that provides the setter for the field to be bound, usually to a `ref`. */
	bindSetter?: (setter: (value: string) => void) => void;
	
	
	/** Whether to output the unmodified field value. This can only be used with `type='datetime-local'`. Defaults to `false`. */
	outputRaw?: boolean;
	
	
	/** Whether to disable password managers or not. Defaults to `false`. */
	disablePasswordManagers?: boolean;
}

type CompleteProps = Props &
	Omit<
		React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
		'id' | 'className' | 'value' | 'defaultValue' | 'type' | 'checked' | 'defaultChecked' | 'prefix' | 'onChange'
	>;


// Function component
const Input = React.forwardRef<HTMLInputElement, CompleteProps>((props: CompleteProps, ref) => {
	// Break off the props we don't want to give to the intrinsic element
	const {
		initialValue,
		icon,
		label,
		note,
		prefix,
		suffix,
		errorMessage: errorMessageProp,
		showStrength,
		onChange: onChangeProp,
		bindGetter,
		bindSetter,
		recoverState: recoverStateProp,
		exclude: excludeProp,
		min: minProp,
		max: maxProp,
		outputRaw,
		disablePasswordManagers,
		...passThroughProps
	} = props;
	
	
	// Use context
	const context = useContext(APIConnectedFormContext);
	
	
	// Decide if the field is excluded
	const exclude = excludeProp ?? passThroughProps.disabled ?? undefined;
	
	
	// Retrieve values from API form's context, falling back to props
	const recoverState = recoverStateProp ?? context?.recoverState;
	const errorMessage = context?.error.field === passThroughProps.name ? context.error.message : errorMessageProp;
	
	
	// Prevent problematic usages that can't be prevented with TypeScript
	if (props.icon && props.icon.prefix !== 'fas' && props.icon.prefix !== 'fab') {
		throw new Error('A field’s icon should come from Font Awesome’s "solid" or "brand" package');
	}
	
	if (passThroughProps.type !== 'password' && showStrength) {
		throw new Error('The `showStrength` prop only works with password inputs.');
	}
	
	if (passThroughProps.type !== 'datetime-local' && outputRaw) {
		throw new Error('The `outputRaw` prop only works with datetime-local inputs.');
	}
	
	
	// Use state
	const [value, setValue] = useState<string>(initialValue || '');
	const [maskedInputHidden, setMaskedInputHidden] = useState(true);
	
	
	// Bind getter/setter
	bindGetter && bindGetter(() => value);
	bindSetter && bindSetter(setValue);
	
	
	// Store whether to output an ISO-8601 string
	const outputISO = Boolean(!outputRaw && passThroughProps.type === 'datetime-local' && value);
	
	
	// Add and remove getter from connected form
	const { name } = passThroughProps;
	let addValueGetter: NonNullable<ContextDetails>['addValueGetter'] | null = null;
	let removeValueGetter: NonNullable<ContextDetails>['removeValueGetter'] | null = null;
	
	if (context) {
		addValueGetter = context.addValueGetter;
		removeValueGetter = context.removeValueGetter;
	}
	
	useEffect(() => {
		if (addValueGetter === null || exclude === true) {
			return;
		}
		
		addValueGetter(name, () => {
			if (outputISO) {
				// Handle large year values
				if (value.length > 16) {
					return new Date(`+${value.padStart(18, '0')}`).toISOString();
				}
				
				
				// Return ISO-8601 string
				return new Date(value).toISOString();
			}
			
			return value;
		});
		
		return () => {
			if (removeValueGetter === null) {
				return;
			}
			
			removeValueGetter(name);
		};
	}, [name, value, addValueGetter, removeValueGetter, exclude, outputISO]);
	
	
	// Generate unique ID
	const uniqueID = useRef(uniqueId('input-field-'));
	
	
	// Handle value changes
	const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
		setValue(event.target.value);
	};
	
	
	// Debounce save value function
	const debouncedSaveValue = useCallback(debounce(saveValue, 300), []); // eslint-disable-line react-hooks/exhaustive-deps
	
	
	// Save new value on updates
	useUpdateOnlyEffect(() => {
		props.type !== 'password' &&
			recoverState &&
			recoverState.enabled &&
			debouncedSaveValue(passThroughProps.name, value, recoverState.uniqueIdentifier);
	}, [value]);
	
	
	// Notify parent of changes
	useUpdateOnlyEffect(() => {
		onChangeProp?.(outputISO ? new Date(value).toISOString() : value);
		
		context?.onChange?.(true);
	}, [value, outputISO]);
	
	useUpdateOnlyEffect(() => {
		context?.onExcludeChange?.();
	}, [exclude]);
	
	
	// Use ref
	const hasMounted = useRef(false);
	
	
	// Attempt to recover state on mount
	if (recoverState && recoverState.enabled && hasMounted.current === false) {
		hasMounted.current = true;
		
		window.setTimeout(() => {
			const recoveredValue = parsePersistentFormValue(recoverState.uniqueIdentifier, passThroughProps.name, z.string());
			
			if (typeof recoveredValue !== 'undefined') {
				setValue(recoveredValue);
			}
		}, 1); // Must happen asynchronously
		
		/* Don't trigger onChange here; it will be triggered before all getters are bound, and cause issues */
	}
	
	
	// Special handling for masked inputs
	let toggleMaskingLink = null;
	let isPrivate = false;
	
	if (passThroughProps.type === 'password') {
		isPrivate = true;
		
		toggleMaskingLink = (
			<small
				style={{
					textDecoration: 'underline',
					cursor: 'pointer',
					outline: 'none',
				}}
				{...helperFunctions.buttonize(() => setMaskedInputHidden(!maskedInputHidden))}
				tabIndex={-1}
			>
				{maskedInputHidden ? 'Reveal' : 'Hide'}
			</small>
		);
		
		if (!maskedInputHidden) {
			passThroughProps.type = 'text';
		}
	}
	
	
	// Helper function that constrains a UTC date for use locally
	const convertToZonedDatetimeLocalConstraint = useCallback((iso: string) => {
		// Initialize
		const dateToBeConstrained = new Date(iso);
		
		
		// Prevent years outside of the allowed constraints
		if (dateToBeConstrained.getFullYear() === 0 || dateToBeConstrained.getFullYear() === 9999) {
			dateToBeConstrained.setMinutes(dateToBeConstrained.getMinutes() - dateToBeConstrained.getTimezoneOffset());
		}
		
		
		// Return constrained value
		return dateToBeConstrained.toISOString().slice(0, 16);
	}, []);
	
	
	// Build min/max values
	const min = useMemo(() => {
		// Initialize
		let min = minProp;
		
		
		// Only do special handling if a min was not provided
		if (!min) {
			// Handle earliest allowed date
			if (passThroughProps.type === 'date') {
				min = '0000-01-01';
			}
			
			
			// Handle earliest allowed datetime
			if (passThroughProps.type === 'datetime-local') {
				min = convertToZonedDatetimeLocalConstraint('0000-01-01T00:00:00.000Z');
			}
		}
		
		
		// Return
		return min;
	}, [passThroughProps.type, minProp, convertToZonedDatetimeLocalConstraint]);
	
	const max = useMemo(() => {
		// Initialize
		let max = maxProp;
		
		
		// Only do special handling if a max was not provided
		if (!max) {
			// Handle latest allowed date
			if (passThroughProps.type === 'date') {
				max = '9999-12-31';
			}
			
			
			// Handle latest allowed datetime
			if (passThroughProps.type === 'datetime-local') {
				max = convertToZonedDatetimeLocalConstraint('9999-12-31T23:59:59.999Z');
			}
		}
		
		
		// Return
		return max;
	}, [passThroughProps.type, maxProp, convertToZonedDatetimeLocalConstraint]);
	
	
	// Create theme
	const theme = {
		hasError: Boolean(errorMessage),
	};
	
	
	// Return JSX
	return (
		<ThemeProvider theme={theme}>
			<FieldContainer style={passThroughProps.type === 'hidden' ? { display: 'none' } : undefined}>
				{label && (
					<Label htmlFor={uniqueID.current}>
						{label}
						{toggleMaskingLink}
						{note && <small>{note}</small>}
					</Label>
				)}
				
				<ControlGroup>
					{prefix && <AddOn>{prefix}</AddOn>}
					
					<InputContainer>
						{icon && (
							<InputIconHolder>
								<FontAwesomeIcon icon={icon} />
							</InputIconHolder>
						)}
						
						<input
							{...passThroughProps}
							id={uniqueID.current}
							value={value}
							onChange={handleChange}
							ref={ref}
							min={min}
							max={max}
							data-private={isPrivate || undefined}
							aria-invalid={Boolean(errorMessage)}
							aria-describedby={`error-field-${uniqueID.current}`}
							{...(['password', 'email'].includes(passThroughProps.type)
								? {
										spellCheck: 'false',
										autoCorrect: 'off',
									}
								: {})}
							{...(isScreenshot() ? { spellCheck: 'false' } : {})}
							{...(disablePasswordManagers ? passwordManagerDisablingProps : {})}
						/>
					</InputContainer>
					
					{suffix && <AddOn>{suffix}</AddOn>}
				</ControlGroup>
				
				{showStrength && (
					<Suspense fallback={<SuspenseFallback />}>
						<PasswordStrengthMeter password={value} alreadyHasError={Boolean(errorMessage)} />
					</Suspense>
				)}
				
				{errorMessage && <FieldErrorMessage id={`error-field-${uniqueID.current}`} message={errorMessage} />}
			</FieldContainer>
		</ThemeProvider>
	);
});


// @ts-ignore: displayName does exist
Input.displayName = 'Input';

export default Input;
